import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DOSSIER_SCENARIO_LABELS,
  DOSSIER_STATUT_LABELS,
  DossierStatutSchema,
  type DossierDto,
  type DossierStatut,
} from '../../lib/api-types';
import type { DossierSortField } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Dialog } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/Table';
import { useMandants } from '../mandants/hooks';
import { useCreateDossier, useDossiers } from './hooks';
import { DossierCreateForm } from './DossierCreateForm';
import { DossierCreateWizard } from './DossierCreateWizard';

const PAGE_SIZE = 10;

const SORT_FIELDS: { value: DossierSortField; label: string }[] = [
  { value: 'createdAt', label: 'Date de création' },
  { value: 'updatedAt', label: 'Dernière modification' },
  { value: 'numero', label: 'Numéro' },
  { value: 'statut', label: 'Statut' },
];

export function DossiersListPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState<DossierStatut | ''>('');
  const [mandantId, setMandantId] = useState('');
  const [sortField, setSortField] = useState<DossierSortField>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [createOpen, setCreateOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [created, setCreated] = useState<DossierDto | null>(null);

  // One Idempotency-Key per create submission, generated when the dialog opens
  // and reused across retries so a network retry cannot duplicate the dossier.
  const idempotencyKey = useRef<string>('');

  const mandantsQuery = useMandants({ page: 1, pageSize: 100, includeInactive: true });
  const query = useDossiers({
    page,
    pageSize: PAGE_SIZE,
    search,
    statut,
    mandantId,
    sort: `${sortField}:${sortDir}`,
  });
  const createDossier = useCreateDossier();

  const openCreate = () => {
    idempotencyKey.current = crypto.randomUUID();
    createDossier.reset();
    setCreateOpen(true);
  };

  const openWizard = () => {
    idempotencyKey.current = crypto.randomUUID();
    createDossier.reset();
    setWizardOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Dossiers</h1>
        <div className="flex gap-2">
          <Button onClick={openWizard}>Création guidée</Button>
          <Button variant="outline" onClick={openCreate}>
            Nouveau dossier
          </Button>
        </div>
      </div>

      {created ? (
        <Alert variant="success" title="Dossier créé">
          Numéro officiel :{' '}
          <Link to={`/dossiers/${created.id}`} className="font-semibold underline">
            {created.numero}
          </Link>
        </Alert>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-44 flex-1">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="d-search">
                Rechercher
              </label>
              <Input
                id="d-search"
                placeholder="Numéro, objet…"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="d-statut">
                Statut
              </label>
              <Select
                id="d-statut"
                value={statut}
                onChange={(e) => {
                  setPage(1);
                  setStatut(e.target.value as DossierStatut | '');
                }}
              >
                <option value="">Tous les statuts</option>
                {DossierStatutSchema.options.map((s) => (
                  <option key={s} value={s}>
                    {DOSSIER_STATUT_LABELS[s]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-48">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="d-mandant">
                Mandant
              </label>
              <Select
                id="d-mandant"
                value={mandantId}
                onChange={(e) => {
                  setPage(1);
                  setMandantId(e.target.value);
                }}
              >
                <option value="">Tous les mandants</option>
                {(mandantsQuery.data?.items ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nom}
                  </option>
                ))}
              </Select>
            </div>
            <div className="w-44">
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="d-sort">
                Trier par
              </label>
              <Select
                id="d-sort"
                value={sortField}
                onChange={(e) => setSortField(e.target.value as DossierSortField)}
              >
                {SORT_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              aria-label="Inverser le sens du tri"
            >
              {sortDir === 'asc' ? '↑ Croissant' : '↓ Décroissant'}
            </Button>
          </div>

          {query.isError ? (
            <Alert variant="error">{toDisplayError(query.error).message}</Alert>
          ) : null}

          {query.isLoading ? (
            <div className="py-8 text-center">
              <Spinner label="Chargement des dossiers…" />
            </div>
          ) : query.data && query.data.items.length > 0 ? (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Numéro</TH>
                    <TH>Mandant</TH>
                    <TH>Scénario</TH>
                    <TH>Statut</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {query.data.items.map((d) => (
                    <TR key={d.id}>
                      <TD className="font-mono text-xs font-medium text-slate-900">{d.numero}</TD>
                      <TD>{d.mandantNom}</TD>
                      <TD>{DOSSIER_SCENARIO_LABELS[d.scenario]}</TD>
                      <TD>
                        <Badge tone="info">{DOSSIER_STATUT_LABELS[d.statut]}</Badge>
                      </TD>
                      <TD className="text-right">
                        <Link
                          to={`/dossiers/${d.id}`}
                          className="text-sm font-medium text-brand-600 hover:underline"
                        >
                          Détails
                        </Link>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination
                page={query.data.page}
                totalPages={query.data.totalPages}
                total={query.data.total}
                onPageChange={setPage}
              />
            </>
          ) : (
            <p className="py-8 text-center text-sm text-slate-500">Aucun dossier trouvé.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} title="Nouveau dossier">
        {createDossier.isError ? (
          <Alert
            variant="error"
            className="mb-4"
            details={toDisplayError(createDossier.error).details}
          >
            {toDisplayError(createDossier.error).message}
          </Alert>
        ) : null}
        <DossierCreateForm
          pending={createDossier.isPending}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(input) =>
            createDossier.mutate(
              { input, idempotencyKey: idempotencyKey.current },
              {
                onSuccess: (dossier) => {
                  setCreated(dossier);
                  setCreateOpen(false);
                },
              },
            )
          }
        />
      </Dialog>

      <Dialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        title="Création guidée d’un dossier"
      >
        {createDossier.isError ? (
          <Alert
            variant="error"
            className="mb-4"
            details={toDisplayError(createDossier.error).details}
          >
            {toDisplayError(createDossier.error).message}
          </Alert>
        ) : null}
        <DossierCreateWizard
          pending={createDossier.isPending}
          onCancel={() => setWizardOpen(false)}
          onSubmit={(input) =>
            createDossier.mutate(
              { input, idempotencyKey: idempotencyKey.current },
              {
                onSuccess: (dossier) => {
                  setCreated(dossier);
                  setWizardOpen(false);
                },
              },
            )
          }
        />
      </Dialog>
    </div>
  );
}
